// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { classToPlain, plainToClass } from 'class-transformer';

// Constant imports
import { URLConstants } from '../../_constants/url-constants';
import { dateJSONTransformer } from '../../_constants/validation-format-constants';

// Model imports
import { AnnouncementsResultDto } from '../../_models/announcements-result-dto';
import { AnnouncementIdParametersDto } from '../../_models/announcement-id-parameters-dto';
import { AnnouncementEditorResultDto } from '../../_models/announcement-editor-result-dto';
import { AnnouncementRawDto } from '../../_models/announcement-raw-dto';
import { AnnouncementAccessResultDto } from '../../_models/announcement-access-result-dto';

@Injectable({
    providedIn: 'root'
})
export class PortalAnnouncementService {
    constructor(private readonly httpClient: HttpClient) {}

    getAnnouncementCount(): Observable<number> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}/unread/count`;
        return this.httpClient.get(url).pipe(map((response: { count: number }) => response.count));
    }

    getAnnouncementAccess(): Observable<AnnouncementAccessResultDto> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}/access`;
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(AnnouncementAccessResultDto, response)));
    }

    getAnnouncements(category: string): Observable<AnnouncementsResultDto[]> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}/${category}`;
        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(AnnouncementsResultDto, response)));
    }

    getExpiredAnnouncements(expiredFromDate: Date, expiredToDate: Date): Observable<AnnouncementsResultDto[]> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}/expired?expiredFromDate=${dateJSONTransformer(
            expiredFromDate
        )}&expiredToDate=${dateJSONTransformer(expiredToDate)}`;
        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(AnnouncementsResultDto, response)));
    }

    getAnnouncementEditor(announcementId?: number): Observable<AnnouncementEditorResultDto> {
        let url = `${URLConstants.ccPortalAnnouncementApiUrl}/editor`;
        if (announcementId > 0) {
            url = `${url}?announcementId=${announcementId}`;
        }
        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(AnnouncementEditorResultDto, response)));
    }

    saveAnnouncement(params: AnnouncementRawDto): Observable<any> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}`;
        return this.httpClient.post(url, classToPlain(params));
    }

    updateAnnouncement(params: AnnouncementRawDto): Observable<any> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}`;
        return this.httpClient.put(url, classToPlain(params));
    }

    deleteAnnouncement(params: AnnouncementIdParametersDto): Observable<any> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(params)
        };
        return this.httpClient.delete(url, options);
    }

    markAsReadAnnouncements(param: AnnouncementIdParametersDto): Observable<any> {
        const url = `${URLConstants.ccPortalAnnouncementApiUrl}/markasread`;
        return this.httpClient.put(url, classToPlain(param));
    }
}
